import { useCallback, useRef } from "react";

import { AutoComplete, Form, Input, SelectProps } from "antd";

import { RVFormItem } from "@regal-voice/ui";
import { useExternalTransferPhoneNumbers } from "Pages/settings/Pages/ExternalTransferPhonebook/Hooks/useExternalTransferPhoneNumbers";
import { formatPhoneNumber } from "Services/CommunicationService";
import { ExternalPhoneNumber } from "Types/ExternalPhoneNumber";

import styles from "./ExternalTransfer.module.scss";

export function ExternalTransfer(): JSX.Element {
    const form = Form.useFormInstance();
    const EXTERNAL_PHONE_NUMBER_RULES = [{ required: true, message: "The number field is required" }];

    const searchableFromNumberWrapper = useRef<HTMLDivElement>(null);
    const { phoneNumbersData } = useExternalTransferPhoneNumbers({ isManagerContext: false });

    const handleOnSelect = useCallback(
        (value: string) => {
            form.setFieldsValue({
                externalPhoneNumber: formatPhoneNumber(+value.split("-")[0]),
                phoneSelect: value.split("-")[1],
            });
        },
        [form]
    );

    const handleOnChange = useCallback(
        (value: string) => {
            let newValue = value;
            // no need to unnecessarily parse/format so early
            if (value.length >= 2) {
                newValue = formatPhoneNumber(value);
            }
            form.setFieldsValue({
                externalPhoneNumber: newValue,
                phoneSelect: newValue,
            });
        },
        [form]
    );

    const filterOption: SelectProps<string>["filterOption"] = (input, option) => {
        if (!option) {
            return false;
        }

        return (option.value as string)?.toLocaleLowerCase().includes(input.replace("+1 ", "").toLocaleLowerCase());
    };

    const options = phoneNumbersData
        .filter((item: Partial<ExternalPhoneNumber>) => item.phoneNumber && item.integration !== "five9")
        .map((item: Partial<ExternalPhoneNumber>, index: number) => ({
            key: index,
            value: `${item.phoneNumber}-${item.name}`,
            label: (
                <div>
                    <div className={styles.name}>{item.name}</div>
                    <div className={styles.number}>
                        <span>{formatPhoneNumber(item.phoneNumber)}</span>
                    </div>
                </div>
            ),
        }));

    return (
        <div ref={searchableFromNumberWrapper}>
            <RVFormItem name="phoneSelect" rules={EXTERNAL_PHONE_NUMBER_RULES}>
                <AutoComplete
                    data-testid="external-transfer-search-input"
                    popupClassName="certain-category-search-dropdown"
                    options={options}
                    getPopupContainer={() => searchableFromNumberWrapper.current as HTMLElement}
                    filterOption={filterOption}
                    onSelect={handleOnSelect}
                    onChange={handleOnChange}
                >
                    <Input size="large" placeholder="External Phone Number" data-testid="enter-phone-number" />
                </AutoComplete>
            </RVFormItem>
            <RVFormItem name="externalPhoneNumber" hidden>
                <Input hidden data-testid="external-phone-number-value" />
            </RVFormItem>
        </div>
    );
}
